// Public surface for disappearing messages.
//
// Ported from DisappearingMessages.swift. The chat composer / chat view talk
// to this — never directly to the underlying store, payload, or scheduler.
// That gives us one place to:
//
//   - apply the per-channel timer when sending,
//   - schedule the local wipe deadline on send and on receive,
//   - reconcile a peer's "timer changed" control message,
//
// without the call sites having to coordinate three modules in lockstep.

import { DisappearingMessageDuration } from "./DisappearingMessageDuration";
import { DisappearingMessageStore } from "./DisappearingMessageStore";
import { DisappearingMessageWireFormat } from "./DisappearingMessagePayload";
import {
  MessageExpiryScheduler,
  DefaultMessageWiper,
} from "./MessageExpiryScheduler";

const DEFAULT_STORE_DB = "kaether-disappearing-timers";
const DEFAULT_SCHEDULER_DB = "kaether-expiry-scheduler";

export class DisappearingMessages {
  private readonly timerStore: DisappearingMessageStore;
  private readonly scheduler: MessageExpiryScheduler;

  constructor(
    timerStore: DisappearingMessageStore = new DisappearingMessageStore(DEFAULT_STORE_DB),
    scheduler: MessageExpiryScheduler = new MessageExpiryScheduler(
      DEFAULT_SCHEDULER_DB,
      new DefaultMessageWiper(),
    ),
  ) {
    this.timerStore = timerStore;
    this.scheduler = scheduler;
  }

  // -------------------------------------------------------------------------
  // Timer management

  async currentTimer(channelId: string): Promise<DisappearingMessageDuration> {
    return this.timerStore.getTimer(channelId);
  }

  /**
   * Apply a local timer change. Caller is responsible for emitting the
   * over-the-wire control message that informs peers (see docs/
   * disappearing-messages.md → "Timer-change control message").
   */
  async setLocalTimer(
    channelId: string,
    duration: DisappearingMessageDuration,
  ): Promise<void> {
    await this.timerStore.setTimer(channelId, duration, null);
  }

  /**
   * Apply a remote timer change received as a control message. Last-write-
   * wins on the timestamp so two clients changing the timer simultaneously
   * converge to the latest one rather than flapping.
   */
  async applyRemoteTimer(
    channelId: string,
    duration: DisappearingMessageDuration,
    fromUserId: string,
    at: Date,
  ): Promise<void> {
    const existing = await this.timerStore.record(channelId);
    if (existing !== null && existing.updatedAt > at.getTime()) {
      return; // we already have a newer setting
    }
    await this.timerStore.setTimer(channelId, duration, fromUserId, at);
  }

  // -------------------------------------------------------------------------
  // Send path

  /**
   * Wrap the plaintext for encryption. Returns the string the caller passes to
   * E2EEManager.encrypt — either the raw text (timer off) or a
   * `kaether:eph:v1:` JSON payload (timer on).
   */
  async wrapForSend(
    plaintext: string,
    channelId: string,
    sentAt: Date = new Date(),
  ): Promise<string> {
    const timer = await this.timerStore.getTimer(channelId);
    return DisappearingMessageWireFormat.wrap(plaintext, timer, sentAt);
  }

  /**
   * Schedule the local copy of an outgoing message for expiry. Call this
   * after the encrypt+send has succeeded so we don't track messages that the
   * server rejected.
   */
  async trackOutgoing(
    messageId: string,
    channelId: string,
    encryptedContent: string,
    sentAt: Date = new Date(),
  ): Promise<void> {
    const timer = await this.timerStore.getTimer(channelId);
    if (timer.isOff) return;
    const expiresAt = new Date(sentAt.getTime() + timer.ms);
    await this.scheduler.track(messageId, channelId, encryptedContent, expiresAt);
  }

  // -------------------------------------------------------------------------
  // Receive path

  /**
   * Unwrap a freshly-decrypted plaintext. Returns the rendered text plus the
   * deadline (if the message carried a TTL). Caller renders `text` in the UI
   * and, when `expiresAt` is non-null, calls `trackIncoming`.
   */
  unwrapReceived(decryptedText: string): { text: string; expiresAt: Date | null } {
    const unwrapped = DisappearingMessageWireFormat.unwrap(decryptedText);
    if (unwrapped.payload === null) {
      return { text: unwrapped.text, expiresAt: null };
    }
    const { ttlSeconds, sentAt } = unwrapped.payload;
    const expiresAt =
      ttlSeconds > 0 ? new Date(sentAt + ttlSeconds * 1000) : null;
    return { text: unwrapped.text, expiresAt };
  }

  async trackIncoming(
    messageId: string,
    channelId: string,
    encryptedContent: string,
    expiresAt: Date,
  ): Promise<void> {
    await this.scheduler.track(messageId, channelId, encryptedContent, expiresAt);
  }

  // -------------------------------------------------------------------------
  // Lifecycle

  startBackgroundSweeper(): void {
    this.scheduler.start();
  }

  stopBackgroundSweeper(): void {
    this.scheduler.stop();
  }

  // -------------------------------------------------------------------------
  // Diagnostics

  async pendingExpiryCount(): Promise<number> {
    return (await this.scheduler.pendingEntries()).length;
  }
}
